import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ALL_PRESETS,
  findPresetForDrone,
  type DronePreset,
  type CameraSpec,
} from "@client/lib/dronePresets";
import {
  SHUTTER_FPS_OPTIONS,
  SHUTTER_ANGLES,
  MANUAL_SHUTTER_DENOMS,
  recommendNd,
  estimateEv,
  shutterDenomFromEv,
  snapShutterDenom,
  classifySky,
  sunElevationDeg,
  SKY_LABEL_DE,
  type ShutterFps,
  type ShutterAngle,
} from "@client/lib/ndFilter";

/**
 * ND-Filter-Finder fürs Web. Spiegelt den mobilen NdFilterFinderScreen 1:1
 * (Drohne, Kamera, Ziel-Framerate, Shutter ermitteln, Empfehlung) und nutzt
 * dieselbe RN-freie Logik aus `@client/lib/ndFilter`. Die mobile FeatureGate
 * entfällt hier — das Web regelt die Freischaltung an anderer Stelle.
 */

type Drone = {
  id: string;
  name: string;
  manufacturer: string | null;
  model: string | null;
  droneType?: string;
  ownedNdFilters?: string[] | null;
};

type Mode = "manual" | "auto";

interface DroneOption {
  key: string;
  label: string;
  preset: DronePreset;
  source: "profile" | "preset";
}

export default function NdFilterFinder() {
  const { data: drones = [], isLoading } = useQuery<Drone[]>({ queryKey: ["/api/drones"] });

  const [selectedDroneKey, setSelectedDroneKey] = useState<string | null>(null);
  const [fps, setFps] = useState<ShutterFps>(24);
  const [shutterAngle, setShutterAngle] = useState<ShutterAngle>(180);
  const [shutterAngleUserSet, setShutterAngleUserSet] = useState(false);
  const [mode, setMode] = useState<Mode>("manual");
  const [manualDenom, setManualDenom] = useState(1000);
  const [selectedCameraIndex, setSelectedCameraIndex] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);

  const droneOptions: DroneOption[] = useMemo(() => {
    const opts: DroneOption[] = [];
    for (const d of drones) {
      const preset = findPresetForDrone(d.name, d.manufacturer ?? undefined, d.model ?? undefined);
      if (preset) {
        opts.push({ key: `profile:${d.id}`, label: `${d.name} (Profil)`, preset, source: "profile" });
      }
    }
    if (opts.length === 0) {
      for (const p of ALL_PRESETS) {
        if (!p.apertureValue) continue;
        opts.push({ key: `preset:${p.manufacturer}-${p.model}`, label: `${p.manufacturer} ${p.model}`, preset: p, source: "preset" });
      }
    }
    return opts;
  }, [drones]);

  const selectedOption = useMemo(
    () => droneOptions.find((o) => o.key === selectedDroneKey) ?? droneOptions[0] ?? null,
    [droneOptions, selectedDroneKey],
  );
  const basePreset = selectedOption?.preset ?? null;

  useEffect(() => {
    setSelectedCameraIndex(0);
  }, [selectedOption?.key]);

  const cameras: CameraSpec[] = basePreset?.cameras ?? [];
  const hasMultipleCameras = cameras.length > 1;
  const activeCamera: CameraSpec | null = hasMultipleCameras
    ? (cameras[selectedCameraIndex] ?? cameras[0])
    : null;

  const preset: DronePreset | null = useMemo(() => {
    if (!basePreset) return null;
    if (!activeCamera) return basePreset;
    return {
      ...basePreset,
      sensor: activeCamera.sensor,
      apertureValue: activeCamera.apertureValue,
      hasVariableAperture: activeCamera.hasVariableAperture ?? false,
      fovDeg: activeCamera.fovDeg,
      focalLength35mmEquiv: activeCamera.focalLength35mmEquiv,
      filterEcosystem: activeCamera.filterEcosystem ?? basePreset.filterEcosystem,
    };
  }, [basePreset, activeCamera]);

  const ownedFromProfile: string[] | null = useMemo(() => {
    if (selectedOption?.source !== "profile") return null;
    if (selectedDroneKey && selectedOption.key !== selectedDroneKey) return null;
    const id = selectedOption.key.replace(/^profile:/, "");
    const drone = drones.find((d) => d.id === id);
    return drone?.ownedNdFilters && drone.ownedNdFilters.length > 0 ? drone.ownedNdFilters : null;
  }, [selectedOption, selectedDroneKey, drones]);

  const effectiveEcosystem: string[] | undefined = ownedFromProfile ?? preset?.filterEcosystem ?? undefined;

  // ─── Auto-Modus: Wetter & Sonnenstand ──────────────────────────────────────
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoError, setAutoError] = useState<string | null>(null);
  const [autoCloud, setAutoCloud] = useState<number | null>(null);
  const [autoRain, setAutoRain] = useState<number>(0);
  const [autoSunElev, setAutoSunElev] = useState<number | null>(null);
  const [autoDenom, setAutoDenom] = useState<number | null>(null);

  const refreshAuto = useCallback(async () => {
    if (!preset?.apertureValue) {
      setAutoError("Diese Drohne hat keine bekannte Blende — Auto-Modus nicht möglich.");
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setAutoError("Standort im Browser nicht verfügbar. Bitte den manuellen Modus verwenden.");
      return;
    }
    setAutoLoading(true);
    setAutoError(null);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 300000,
        });
      });
      const lat = pos.coords.latitude;
      const lon = pos.coords.longitude;
      let cloud = 50;
      let rain = 0;
      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=cloud_cover,precipitation`;
        const res = await fetch(url);
        const json = await res.json();
        cloud = json?.current?.cloud_cover ?? 50;
        rain = json?.current?.precipitation ?? 0;
      } catch {
        cloud = 50;
        rain = 0;
      }
      setAutoCloud(cloud);
      setAutoRain(rain);
      const sunElev = sunElevationDeg(lat, lon, new Date());
      setAutoSunElev(sunElev);
      const ev = estimateEv(cloud, rain, sunElev);
      const denom = snapShutterDenom(shutterDenomFromEv(ev, preset.apertureValue));
      setAutoDenom(denom);
    } catch (err) {
      const message = err instanceof GeolocationPositionError
        ? "Standortzugriff verweigert oder nicht verfügbar. Bitte den manuellen Modus verwenden."
        : (err as Error)?.message || "Auto-Schätzung fehlgeschlagen.";
      setAutoError(message);
    } finally {
      setAutoLoading(false);
    }
  }, [preset]);

  useEffect(() => {
    if (mode === "auto" && autoDenom == null && !autoLoading && !autoError && preset?.apertureValue) {
      refreshAuto();
    }
  }, [mode, autoDenom, autoLoading, autoError, preset, refreshAuto]);

  // ─── Empfehlung ─────────────────────────────────────────────────────────────
  const measuredDenom = mode === "manual" ? manualDenom : (autoDenom ?? manualDenom);
  const recommendation = useMemo(() => {
    if (!preset) return null;
    return recommendNd({
      fps,
      measuredShutterDenom: measuredDenom,
      filterEcosystem: effectiveEcosystem ?? null,
      shutterAngleDeg: shutterAngle,
    });
  }, [preset, fps, measuredDenom, effectiveEcosystem, shutterAngle]);

  useEffect(() => {
    if (shutterAngleUserSet) return;
    const isFpv = preset?.droneType === "fpv_only" || preset?.droneType === "hybrid_fpv";
    setShutterAngle(isFpv ? 270 : 180);
  }, [preset, shutterAngleUserSet]);

  const snappedManual = snapShutterDenom(manualDenom);
  const denomIndex = MANUAL_SHUTTER_DENOMS.indexOf(snappedManual);
  const safeDenomIndex = denomIndex >= 0 ? denomIndex : MANUAL_SHUTTER_DENOMS.indexOf(1000);

  const skyForUi = autoCloud != null && autoSunElev != null
    ? classifySky(autoCloud, autoRain, autoSunElev)
    : null;

  const targetShutter = Math.round((fps * 360) / shutterAngle);
  const isFpv = preset?.droneType === "fpv_only" || preset?.droneType === "hybrid_fpv";

  const fpsHint = useMemo(() => {
    let title: string | null = null;
    let body: string | null = null;
    if (isFpv && fps <= 30) {
      title = "FPV mit Cine-Framerate";
      body = "Schnelle Kurven und Dives wirken bei 24/25/30 fps oft ruckelig oder zerreissen. Für FPV werden 50/60 fps (flüssig) oder 120 fps (Slow-Mo) empfohlen.";
    } else if (!isFpv && fps >= 60) {
      title = "Action-Look";
      body = "Sehr flüssig — ideal für Sport, B-Roll oder spätere Zeitlupe. Für epische Landschafts- oder Cinematic-Shots erzeugen 24/25 fps den natürlicheren Kino-Look.";
    } else if (fps === 120) {
      title = "Super Slow-Mo (120 fps)";
      body = "Zielshutter 1/240 s — die Kamera schluckt von Natur aus viel Licht. Oft reicht ein schwacher ND oder gar keiner.";
    } else if (fps === 25) {
      title = "PAL / Europa-TV (25 fps)";
      body = "Synchron zum 50-Hz-Netz, verhindert Flackern bei Kunstlicht. Für reine Online-Inhalte tut's auch 24 oder 30 fps.";
    } else if (fps === 30) {
      title = "Web-Standard (30 fps)";
      body = "Allrounder für YouTube, Instagram, Tutorials. Wirkt nahbar und realitätsnah, dabei flüssiger als 24 fps.";
    } else if (fps === 24) {
      title = "Kino-Look (24 fps)";
      body = "Klassische Filmrate. Schwenks bewusst langsam ausführen, sonst entsteht Stottern (Judder).";
    } else if (fps === 50 || fps === 60) {
      title = "Flüssiger Look (50/60 fps)";
      body = "Gestochen scharf, ideal für Action und FPV. Im Schnitt auf 24/25 fps verlangsamt = saubere 2× Zeitlupe.";
    }
    if (!title) return null;
    return { title, body, isWarn: isFpv && fps <= 30 };
  }, [isFpv, fps]);

  if (isLoading) {
    return <div className="text-center text-[var(--color-app-text-secondary)] py-10">Lädt …</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-[var(--color-app-text-secondary)] leading-relaxed">
          Finde nach der 180°-Shutter-Regel den passenden ND-Filter: Drohne wählen,
          Ziel-Framerate setzen, Shutter eingeben oder per Wetter schätzen lassen.
        </p>
        <button
          type="button"
          className="btn-secondary text-xs px-3 py-1.5 shrink-0"
          onClick={() => setInfoOpen(true)}
          data-testid="button-nd-help"
        >
          Hilfe
        </button>
      </div>

      {/* Schritt 1 — Drohne */}
      <Section title="1. Drohne">
        {droneOptions.length === 0 ? (
          <div className="card text-sm text-[var(--color-app-text-secondary)]">
            Keine Drohne mit bekannten optischen Daten gefunden. Lege im Pilotenprofil
            eine DJI-Drohne an (z. B. Mini 4 Pro) oder wähle ein Preset.
          </div>
        ) : (
          <>
            <select
              className="input"
              value={selectedOption?.key ?? ""}
              onChange={(e) => {
                setSelectedDroneKey(e.target.value);
                setAutoDenom(null);
              }}
              data-testid="select-drone"
            >
              {droneOptions.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            {preset ? (
              <p className="text-xs text-[var(--color-app-text-secondary)] mt-2">
                {preset.sensor ?? "—"} · ƒ/{preset.apertureValue?.toFixed(1) ?? "?"}
                {preset.hasVariableAperture ? " (variabel)" : ""}
                {preset.focalLength35mmEquiv ? ` · ${preset.focalLength35mmEquiv} mm` : ""}
              </p>
            ) : null}
            {preset?.hasVariableAperture ? (
              <div className="mt-3 bg-brand-50 border border-brand-100 rounded-md p-3 text-sm text-[var(--color-app-text)]">
                Diese Drohne hat eine variable Blende. Du kannst alternativ die Blende
                schließen (z. B. ƒ/8) statt einen ND-Filter zu verwenden.
              </div>
            ) : null}

            {hasMultipleCameras ? (
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-wide text-[var(--color-app-text-tertiary)] mb-1.5">
                  1B. Kamera
                </div>
                <select
                  className="input"
                  value={selectedCameraIndex}
                  onChange={(e) => {
                    setSelectedCameraIndex(Number(e.target.value));
                    setAutoDenom(null);
                  }}
                  data-testid="select-camera"
                >
                  {cameras.map((cam, idx) => (
                    <option key={`${cam.name}-${idx}`} value={idx}>
                      {cam.name} · {cam.sensor} · ƒ/{cam.apertureValue.toFixed(1)}
                      {cam.focalLength35mmEquiv ? ` · ${cam.focalLength35mmEquiv} mm` : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </>
        )}
      </Section>

      {/* Schritt 2 — Ziel-Framerate */}
      <Section title="2. Ziel-Framerate">
        <div className="flex flex-wrap gap-2">
          {SHUTTER_FPS_OPTIONS.map((f) => {
            const active = fps === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFps(f)}
                className={
                  "px-4 py-2 rounded-md text-sm font-bold border " +
                  (active
                    ? "bg-brand-600 border-brand-600 text-white"
                    : "bg-[var(--color-app-bg-secondary)] border-[var(--color-app-border)] text-[var(--color-app-text)] hover:bg-[var(--color-app-bg-tertiary)]")
                }
                data-testid={`fps-${f}`}
              >
                {f}
              </button>
            );
          })}
        </div>
        <p className="text-sm text-[var(--color-app-text-secondary)] mt-3">
          Zielshutter ({shutterAngle === 180 ? "180°-Regel" : `${shutterAngle}°-Shutter`}):{" "}
          <span className="font-bold text-[var(--color-app-text)]">1/{targetShutter} s</span>
        </p>

        <div className="text-xs font-semibold text-[var(--color-app-text-secondary)] mt-4 mb-1.5">
          Shutter-Winkel (Motion-Blur-Stil):
        </div>
        <div className="flex flex-wrap gap-2">
          {SHUTTER_ANGLES.map((a) => {
            const active = shutterAngle === a;
            const label = a === 180 ? "180° Cinematic" : a === 270 ? "270° FPV-Look" : "360° Max-Blur";
            return (
              <button
                key={a}
                type="button"
                onClick={() => {
                  setShutterAngle(a);
                  setShutterAngleUserSet(true);
                }}
                className={
                  "px-3 py-2 rounded-md text-xs font-bold border " +
                  (active
                    ? "bg-brand-600 border-brand-600 text-white"
                    : "bg-[var(--color-app-bg-secondary)] border-[var(--color-app-border)] text-[var(--color-app-text)] hover:bg-[var(--color-app-bg-tertiary)]")
                }
                data-testid={`shutter-angle-${a}`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {shutterAngle !== 180 ? (
          <p className="text-xs text-[var(--color-app-text-tertiary)] mt-2">
            {shutterAngle === 270
              ? "+0.6 Stops dunkler als 180° — stärkeres ND nötig. Kaschiert Vibrationen und Prop-Wash, typischer FPV-Freestyle-Look."
              : "+1.0 Stops dunkler als 180° — maximaler Blur, nur für sehr ruhige Schwenks geeignet."}
          </p>
        ) : null}

        {fpsHint ? (
          <div
            className={
              "mt-3 rounded-md border p-3 text-sm " +
              (fpsHint.isWarn
                ? "bg-[var(--color-caution-bg)] border-[var(--color-caution)] text-[var(--color-caution)]"
                : "bg-[var(--color-app-bg-secondary)] border-[var(--color-app-border)] text-[var(--color-app-text)]")
            }
          >
            <div className="font-bold">{fpsHint.title}</div>
            <div className="text-[var(--color-app-text-secondary)]">{fpsHint.body}</div>
          </div>
        ) : null}
      </Section>

      {/* Schritt 3 — Shutter ermitteln */}
      <Section title="3. Shutter ermitteln">
        <div className="inline-flex rounded-md bg-[var(--color-app-bg-tertiary)] p-1 mb-3">
          {(["manual", "auto"] as Mode[]).map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={
                  "px-4 py-1.5 rounded text-sm font-semibold " +
                  (active ? "bg-brand-600 text-white" : "text-[var(--color-app-text)]")
                }
                data-testid={`mode-${m}`}
              >
                {m === "manual" ? "Manuell" : "Auto (Wetter)"}
              </button>
            );
          })}
        </div>

        {mode === "manual" ? (
          <div className="card">
            <p className="text-sm text-[var(--color-app-text-secondary)]">
              Aktuelle Shutter-Speed der Drohne (ohne ND, ISO 100):
            </p>
            <div className="text-3xl font-extrabold text-[var(--color-app-text)] my-2" data-testid="text-manual-shutter">
              1/{snappedManual} s
            </div>
            <input
              type="range"
              className="w-full accent-brand-600"
              min={0}
              max={MANUAL_SHUTTER_DENOMS.length - 1}
              step={1}
              value={safeDenomIndex}
              onChange={(e) => setManualDenom(MANUAL_SHUTTER_DENOMS[Number(e.target.value)])}
              data-testid="slider-shutter"
            />
            <div className="flex justify-between text-xs text-[var(--color-app-text-tertiary)]">
              <span>1/100</span>
              <span>1/16000</span>
            </div>
            {snappedManual < 250 ? (
              <div className="mt-3 rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-3 text-sm text-[var(--color-caution)]">
                Niedriger Shutter (1/{snappedManual} s) deutet auf wenig Licht —
                ein ND-Filter blockt zusätzliches Licht und ist hier meist unnötig.
                Bei Dämmerung/Nacht: kein ND, stattdessen ISO/Blende anpassen.
              </div>
            ) : null}
          </div>
        ) : (
          <div className="card">
            {autoLoading ? (
              <div className="text-center text-[var(--color-app-text-secondary)] py-6">
                Wetter &amp; Sonnenstand werden ermittelt …
              </div>
            ) : autoError ? (
              <>
                <p className="text-sm text-[var(--color-nogo)]">{autoError}</p>
                <button
                  type="button"
                  className="btn-secondary mt-3"
                  onClick={refreshAuto}
                  data-testid="button-auto-retry"
                >
                  Erneut versuchen
                </button>
              </>
            ) : autoDenom != null && preset?.apertureValue ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Meta label="Bewölkung" value={`${Math.round(autoCloud ?? 0)} %`} />
                  <Meta label="Regen" value={`${(autoRain ?? 0).toFixed(1)} mm/h`} />
                  <Meta label="Sonne" value={`${autoSunElev?.toFixed(0) ?? "?"}°`} />
                  <Meta label="Blende" value={`ƒ/${preset.apertureValue.toFixed(1)}`} />
                </div>
                {skyForUi ? (
                  <p className="text-sm text-[var(--color-app-text-secondary)] mt-3">
                    Lichtbedingung:{" "}
                    <span className="font-bold text-[var(--color-app-text)]">{SKY_LABEL_DE[skyForUi]}</span>
                  </p>
                ) : null}
                <p className="text-sm text-[var(--color-app-text-secondary)] mt-3">
                  Geschätzter nativer Shutter (ISO 100):
                </p>
                <div className="text-3xl font-extrabold text-[var(--color-app-text)] my-1" data-testid="text-auto-shutter">
                  1/{autoDenom} s
                </div>
                <button
                  type="button"
                  className="btn-secondary mt-3"
                  onClick={refreshAuto}
                  data-testid="button-auto-refresh"
                >
                  Neu schätzen
                </button>
                <p className="text-xs text-[var(--color-app-text-tertiary)] mt-3">
                  Schätzung über Sunny-16-Regel (ISO 100). Tatsächlicher Drohnen-Shutter
                  kann abweichen — bitte vor dem Flug am Display verifizieren.
                </p>
              </>
            ) : (
              <button type="button" className="btn-primary" onClick={refreshAuto} data-testid="button-auto-load">
                Wetter laden
              </button>
            )}
          </div>
        )}
      </Section>

      {/* Schritt 4 — Empfehlung */}
      <Section title="4. Empfehlung">
        {mode === "auto" && (skyForUi === "night" || skyForUi === "twilight") ? (
          <div className="card bg-[var(--color-app-bg-secondary)]">
            <div className="font-semibold text-[var(--color-app-text-secondary)] mb-1">
              {skyForUi === "night" ? "Nacht erkannt" : "Dämmerung erkannt"}
            </div>
            <div className="text-3xl font-extrabold text-brand-600 mb-2">Kein Filter</div>
            <p className="text-sm text-[var(--color-app-text)]">
              {skyForUi === "night"
                ? "Bei Nacht fehlt Licht — ein ND-Filter würde die Szene nur weiter abdunkeln. Öffne stattdessen die Blende, erhöhe ISO und nutze einen langsameren Shutter. Hinweis: Nachtflug in der Open-Kategorie nur mit grünem Blinklicht zulässig."
                : "In der Dämmerung ist meist zu wenig Licht für die 180°-Regel. Statt ND-Filter: Blende öffnen, ISO erhöhen, ggf. die Belichtungszeit über 1/Frame anheben (cinematischer Look bewusst akzeptieren)."}
            </p>
          </div>
        ) : recommendation && preset ? (
          <div className="card bg-[var(--color-app-bg-secondary)]" data-testid="card-recommendation">
            <div className="text-sm font-semibold text-[var(--color-app-text-secondary)] mb-1">
              {recommendation.ndValue == null
                ? "Empfehlung"
                : recommendation.ecosystemMismatch
                  ? "Ideal / Dein bestes Filter"
                  : "Empfohlener Filter"}
            </div>

            {recommendation.ndValue == null ? (
              <div className="text-3xl font-extrabold text-brand-600">Kein Filter</div>
            ) : recommendation.ecosystemMismatch && recommendation.ecosystemPick ? (
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-2xl font-extrabold text-[var(--color-app-text-tertiary)]">ND{recommendation.ndValue}</span>
                <span className="text-[var(--color-app-text-tertiary)]">→</span>
                <span className="text-3xl font-extrabold text-brand-600">ND{recommendation.ecosystemPick}</span>
              </div>
            ) : (
              <div className="text-3xl font-extrabold text-brand-600">ND{recommendation.ndValue}</div>
            )}

            <p className="text-sm text-[var(--color-app-text)] mt-2">{recommendation.reasoning}</p>

            {effectiveEcosystem && effectiveEcosystem.length > 0 ? (
              <div className="mt-3">
                <div className="text-xs text-[var(--color-app-text-tertiary)]">
                  {ownedFromProfile ? "Dein Bestand:" : "Verfügbar (Hersteller):"}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {effectiveEcosystem.map((nd) => {
                    const active = recommendation.ecosystemPick != null && nd === `ND${recommendation.ecosystemPick}`;
                    return (
                      <span
                        key={nd}
                        className={
                          "px-2 py-0.5 rounded border text-xs font-semibold " +
                          (active
                            ? "bg-brand-100 border-brand-500 text-brand-700"
                            : "border-[var(--color-app-border)] text-[var(--color-app-text-secondary)]")
                        }
                      >
                        {nd}
                      </span>
                    );
                  })}
                </div>
                {!ownedFromProfile && selectedOption?.source === "profile" ? (
                  <p className="text-[11px] italic text-[var(--color-app-text-tertiary)] mt-1.5">
                    Tipp: Pflege deinen eigenen ND-Bestand im Drohnen-Profil — dann
                    bekommst du Vorschläge passend zu deinen tatsächlich vorhandenen Filtern.
                  </p>
                ) : null}
              </div>
            ) : recommendation.ndValue != null && !ownedFromProfile && effectiveEcosystem && effectiveEcosystem.length === 0 ? (
              <div className="mt-3">
                <div className="text-xs text-[var(--color-app-text-tertiary)]">Keine Hersteller-ND-Filter bekannt</div>
                <p className="text-[11px] italic text-[var(--color-app-text-tertiary)] mt-1.5">
                  Für diese Drohne bietet der Hersteller keine passenden ND-Filter an.
                  Der obige Wert gilt als Richtwert für Drittanbieter-Filter.
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Section>

      <a
        href="https://shuttercalculator.com/"
        target="_blank"
        rel="noreferrer"
        className="block text-center text-[11px] text-[var(--color-app-text-tertiary)] hover:underline pt-2"
      >
        180°-Shutter-Regel · Werte gerundet auf Standard-ND-Filter
      </a>

      {infoOpen ? <InfoModal onClose={() => setInfoOpen(false)} /> : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wide text-[var(--color-app-text-tertiary)] mb-2">{title}</div>
      {children}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-md border border-[var(--color-app-border)] px-2 py-1.5 text-center">
      <div className="text-[10px] uppercase text-[var(--color-app-text-secondary)] font-medium">{label}</div>
      <div className="text-sm font-bold text-[var(--color-app-text)]">{value}</div>
    </div>
  );
}

function InfoModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">ND-Filter verstehen</h2>
          <button
            type="button"
            className="text-[var(--color-app-text-tertiary)] hover:text-[var(--color-app-text-secondary)] text-xl leading-none"
            onClick={onClose}
            aria-label="Schließen"
          >
            &times;
          </button>
        </div>

        <div className="space-y-4 text-sm text-[var(--color-app-text-secondary)]">
          <div>
            <h3 className="font-bold text-[var(--color-app-text)]">Warum überhaupt ein ND-Filter?</h3>
            <p>
              Stell dir einen ND-Filter wie eine <strong>Sonnenbrille für deine Drohnen-Kamera</strong> vor.
              An einem hellen Tag trifft so viel Licht auf den Sensor, dass das Bild ohne Gegenmaßnahme
              überbelichtet wäre. Die meisten Drohnen (Mini- oder Air-Serie) haben eine feste Blende und
              können nicht „die Augen zusammenkneifen". Sie müssen deshalb die Belichtungszeit extrem
              verkürzen — genau das ist beim Video das Problem.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-[var(--color-app-text)]">Die drei Kern-Faktoren</h3>
            <h4 className="font-semibold text-[var(--color-app-text)] mt-2">1. Ziel-Framerate &amp; 180°-Regel</h4>
            <p>
              Das menschliche Auge ist an natürliche Bewegungsunschärfe gewöhnt. Damit Video flüssig
              „wie im Kino" wirkt, gilt die 180°-Regel: die Belichtungszeit sollte genau
              <strong> doppelt so schnell </strong> sein wie deine Framerate.
            </p>
            <ul className="list-disc ml-5 mt-1">
              <li>24 fps → Zielshutter 1/48 s</li>
              <li>30 fps → Zielshutter 1/60 s</li>
              <li>60 fps → Zielshutter 1/120 s</li>
            </ul>
            <h4 className="font-semibold text-[var(--color-app-text)] mt-2">2. Der Shutter (Belichtungszeit)</h4>
            <p>
              Ohne Filter wählt die Drohne in der Sonne oft 1/1000 s oder schneller. Das Bild ist
              richtig belichtet, aber die Bewegungsunschärfe fehlt — das Video wirkt abgehackt,
              künstlich scharf und „digital" (Soap-Opera-Effekt).
            </p>
            <h4 className="font-semibold text-[var(--color-app-text)] mt-2">3. Die „Stops" (Lichtreduktion)</h4>
            <p>
              Ein „Stop" halbiert das Licht. Sagt die App dir <strong>4,4 Stops</strong> Reduktion,
              heißt das: es ist ca. 16-mal zu hell für ein kinoreifes Video.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-[var(--color-app-text)]">Was bedeuten die Ergebnisse?</h3>
            <p>
              Die App berechnet die Lücke zwischen dem, was die Drohne <strong>aktuell tun muss</strong>,
              und dem, was für den <strong>Kino-Look optimal</strong> wäre.
            </p>
            <ul className="list-disc ml-5 mt-1">
              <li><strong>ND4 / ND8</strong> — bewölkte Tage, goldene Stunde (leichte Sonnenbrille)</li>
              <li><strong>ND16 / ND32</strong> — normale, sonnige Tage (Standard-Sonnenbrille)</li>
              <li><strong>ND64 / ND128</strong> — greller Sonnenschein, Schnee, Sandstrand (Skibrille)</li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-[var(--color-app-text)]">Schritt-für-Schritt vor dem Start</h3>
            <ol className="list-decimal ml-5 mt-1 space-y-1">
              <li>In der DJI-Fly-App (o. ä.) den Kamera-Modus von <strong>Auto</strong> auf <strong>Manuell (M)</strong> umstellen.</li>
              <li>ISO fest auf <strong>100</strong> stellen — maximale Bildqualität ohne digitales Rauschen.</li>
              <li>Aktuellen Shutter-Wert vom Live-Bild ablesen und hier im Slider eintragen — oder den <strong>Auto-Modus</strong> mit Wetterdaten nutzen.</li>
              <li>Die Empfehlung ablesen (z. B. <strong>ND16</strong>), den passenden Filter aufs Objektiv stecken — fertig für den Kino-Look.</li>
            </ol>
          </div>
        </div>

        <button type="button" className="btn-primary w-full mt-6" onClick={onClose}>
          Verstanden
        </button>
      </div>
    </div>
  );
}
